import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import StatusPill from '../components/StatusPill';
import { ClipboardList, Plus, Search, Clock, AlertTriangle, User, Tag, Calendar, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
const STATUSES = [
    { value: 'new', label: 'New' },
    { value: 'on_discuss', label: 'On Discuss' },
    { value: 'quotation_shared', label: 'Quotation Shared' },
    { value: 'order_process', label: 'Order Process' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
    { value: 'postponed', label: 'Postponed' },
];

const SOURCES = [
    { value: 'retail_signup', label: 'Retail Signup' },
    { value: 'wholesale_application', label: 'Wholesale' },
    { value: 'service_enquiry', label: 'Service Enquiry' },
    { value: 'manual', label: 'Manual' },
];

// map lead status to pill css keys
const STATUS_MAP_TO_PILL = {
    new: 'new', on_discuss: 'in_review', quotation_shared: 'quoted',
    order_process: 'confirmed', success: 'delivered', failed: 'cancelled', postponed: 'packed',
};

const STATUS_COLORS = {
    new: { bg: 'bg-blue-50', text: 'text-blue-800', dot: 'bg-blue-500' },
    on_discuss: { bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
    quotation_shared: { bg: 'bg-violet-50', text: 'text-violet-800', dot: 'bg-violet-500' },
    order_process: { bg: 'bg-sky-50', text: 'text-sky-800', dot: 'bg-sky-500' },
    success: { bg: 'bg-green-50', text: 'text-green-800', dot: 'bg-green-500' },
    failed: { bg: 'bg-red-50', text: 'text-red-800', dot: 'bg-red-500' },
    postponed: { bg: 'bg-orange-50', text: 'text-orange-800', dot: 'bg-orange-500' },
};

function LeadStatusPill({ status }) {
    const s = (status || 'new').toString();
    const c = STATUS_COLORS[s] || STATUS_COLORS.new;
    const label = STATUSES.find(x => x.value === s)?.label || s.replace(/_/g, ' ');
    return (
        <span className={`status-pill ${c.bg} ${c.text}`} data-testid={`lead-status-${s}`}>
            <span className={`status-pill-dot ${c.dot}`} />
            {label}
        </span>
    );
}

function formatDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function toDateInput(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    } catch { return ''; }
}

function LeadDetailDrawer({ open, onOpenChange, leadId, assignees, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState('');
    const [followUp, setFollowUp] = useState('');
    const [assignee, setAssignee] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        if (!leadId) return;
        setLoading(true);
        try {
            const r = await api.get(`/crm/leads/${leadId}`);
            setData(r.data);
            setStatusUpdate(r.data.lead.status);
            setFollowUp(toDateInput(r.data.lead.next_follow_up_at));
            setAssignee(r.data.lead.assignee_id || 'unassigned');
        } finally { setLoading(false); }
    };
    useEffect(() => { if (open && leadId) load(); }, [open, leadId]);

    const saveStatus = async () => {
        setSaving(true);
        try {
            const body = { status: statusUpdate };
            if (statusUpdate === 'postponed') {
                if (!followUp) { toast.error('Please set next follow-up date'); setSaving(false); return; }
                body.next_follow_up_at = new Date(followUp).toISOString();
            } else if (followUp) {
                body.next_follow_up_at = new Date(followUp).toISOString();
            }
            body.assignee_id = assignee === 'unassigned' ? null : assignee;
            await api.patch(`/crm/leads/${leadId}`, body);
            toast.success('Lead updated');
            await load();
            onChanged && onChanged();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
        finally { setSaving(false); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        try {
            await api.post(`/crm/leads/${leadId}/activities`, { note, type: 'note' });
            setNote('');
            toast.success('Note added');
            load(); onChanged && onChanged();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-xl w-full overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>{data?.lead?.subject || 'Lead'}</SheetTitle>
                </SheetHeader>
                {loading || !data ? <LoadingBlock /> : (
                    <div className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-neutral-500">Name:</span> <span className="font-medium">{data.lead.name}</span></div>
                            <div><span className="text-neutral-500">Mobile:</span> <span className="font-mono">{data.lead.mobile}</span></div>
                            <div><span className="text-neutral-500">Email:</span> {data.lead.email || '—'}</div>
                            <div><span className="text-neutral-500">Source:</span> <span className="capitalize">{(data.lead.source || '').replace('_', ' ')}</span></div>
                            {data.lead.business_name && <div className="col-span-2"><span className="text-neutral-500">Business:</span> {data.lead.business_name}</div>}
                            {data.lead.service_type && <div className="col-span-2"><span className="text-neutral-500">Service:</span> {data.lead.service_type}</div>}
                            {data.lead.description && (
                                <div className="col-span-2 p-2 bg-neutral-50 rounded text-neutral-800 text-xs">{data.lead.description}</div>
                            )}
                        </div>

                        <Card className="p-3 space-y-3">
                            <div className="text-sm font-semibold">Update</div>
                            <div>
                                <Label>Status</Label>
                                <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                                    <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {statusUpdate === 'postponed' && (
                                <div>
                                    <Label>Next Follow-up (required for Postponed)</Label>
                                    <Input type="datetime-local" value={followUp} onChange={e => setFollowUp(e.target.value)} data-testid="lead-followup-input" />
                                </div>
                            )}
                            <div>
                                <Label>Assignee</Label>
                                <Select value={assignee} onValueChange={setAssignee}>
                                    <SelectTrigger data-testid="lead-assignee-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unassigned">Unassigned</SelectItem>
                                        {assignees.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={saveStatus} disabled={saving} data-testid="save-lead-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save Changes</Button>
                        </Card>

                        <Card className="p-3 space-y-3">
                            <div className="text-sm font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4" />Add Note</div>
                            <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note about this lead..." data-testid="lead-note-input" />
                            <Button size="sm" onClick={addNote} data-testid="add-note-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Add Note</Button>
                        </Card>

                        <Card className="p-3">
                            <div className="text-sm font-semibold mb-3">Activity Timeline</div>
                            {(!data.activities || data.activities.length === 0) ? (
                                <div className="text-xs text-neutral-500">No activities yet.</div>
                            ) : (
                                <div className="space-y-3" data-testid="lead-activity-timeline">
                                    {data.activities.map((a, idx) => (
                                        <div key={a.id} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5" />
                                                {idx < data.activities.length - 1 && <div className="w-px flex-1 bg-neutral-200 mt-1" />}
                                            </div>
                                            <div className="flex-1 pb-2">
                                                <div className="text-sm">
                                                    {a.type === 'status_change' && a.from_status ? (
                                                        <><span className="text-neutral-600 capitalize">{a.from_status?.replace('_', ' ')}</span> <span>→</span> <span className="font-medium capitalize">{a.to_status?.replace('_', ' ')}</span></>
                                                    ) : (
                                                        <span>{a.note}</span>
                                                    )}
                                                </div>
                                                {a.note && a.type === 'status_change' && <div className="text-xs text-neutral-600 mt-0.5">{a.note}</div>}
                                                <div className="text-xs text-neutral-500 mt-0.5">{formatDate(a.at)} · {a.by || 'system'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function NewLeadDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({ name: '', mobile: '', email: '', subject: '', description: '', tags: '' });
    const submit = async () => {
        try {
            await api.post('/crm/leads', { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] });
            toast.success('Lead added');
            onCreated();
            onOpenChange(false);
            setForm({ name: '', mobile: '', email: '', subject: '', description: '', tags: '' });
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader><DialogTitle>Add Manual Lead</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="manual-lead-name-input" /></div>
                    <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="manual-lead-mobile-input" /></div>
                    <div><Label>Email (optional)</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                    <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="e.g., Enquiry from Instagram" /></div>
                    <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                    <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="vip, referral" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} data-testid="save-manual-lead-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function CRM() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [assignees, setAssignees] = useState([]);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [source, setSource] = useState('all');
    const [assigneeFilter, setAssigneeFilter] = useState('all');
    const [view, setView] = useState('all'); // all | today | overdue | upcoming
    const [loading, setLoading] = useState(true);
    const [drawerLeadId, setDrawerLeadId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [newOpen, setNewOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 300 };
            if (q) params.q = q;
            if (status !== 'all') params.status = status;
            if (source !== 'all') params.source = source;
            if (assigneeFilter === 'unassigned') params.unassigned = true;
            else if (assigneeFilter !== 'all') params.assignee_id = assigneeFilter;
            if (view !== 'all') params.view = view;
            const [lr, sr, ar] = await Promise.all([
                api.get('/crm/leads', { params }),
                api.get('/crm/summary'),
                api.get('/crm/assignees'),
            ]);
            setItems(lr.data.items);
            setSummary(sr.data);
            setAssignees(ar.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [status, source, assigneeFilter, view]);

    const openDrawer = (id) => { setDrawerLeadId(id); setDrawerOpen(true); };

    return (
        <AppShell pageTitle="CRM" pageActions={
            <Button onClick={() => setNewOpen(true)} data-testid="add-lead-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Lead
            </Button>
        }>
            {/* Summary strip */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="kpi-card accent-blue cursor-pointer" onClick={() => setView('all')} data-testid="crm-kpi-total">
                        <div className="text-xs uppercase font-semibold text-neutral-500">Total Leads</div>
                        <div className="text-2xl font-semibold mt-1 tabular-nums">{summary.total}</div>
                    </div>
                    <div className="kpi-card accent-green cursor-pointer" onClick={() => { setView('all'); setStatus('new'); }}>
                        <div className="text-xs uppercase font-semibold text-neutral-500">New</div>
                        <div className="text-2xl font-semibold mt-1 tabular-nums">{summary.by_status.new || 0}</div>
                    </div>
                    <div className="kpi-card accent-amber cursor-pointer" onClick={() => setView('today')} data-testid="crm-kpi-today">
                        <div className="text-xs uppercase font-semibold text-neutral-500 flex items-center gap-1"><Clock className="h-3 w-3" />Today’s Follow-ups</div>
                        <div className="text-2xl font-semibold mt-1 tabular-nums">{summary.today_follow_ups}</div>
                    </div>
                    <div className="kpi-card accent-red cursor-pointer" onClick={() => setView('overdue')} data-testid="crm-kpi-overdue">
                        <div className="text-xs uppercase font-semibold text-neutral-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Overdue</div>
                        <div className="text-2xl font-semibold mt-1 tabular-nums">{summary.overdue}</div>
                    </div>
                    <div className="kpi-card accent-blue cursor-pointer" onClick={() => setAssigneeFilter('unassigned')}>
                        <div className="text-xs uppercase font-semibold text-neutral-500 flex items-center gap-1"><User className="h-3 w-3" />Unassigned</div>
                        <div className="text-2xl font-semibold mt-1 tabular-nums">{summary.unassigned}</div>
                    </div>
                </div>
            )}

            {/* Views */}
            <Card className="p-3 sm:p-4 mb-4">
                <Tabs value={view} onValueChange={setView}>
                    <TabsList>
                        <TabsTrigger value="all" data-testid="view-all">All Leads</TabsTrigger>
                        <TabsTrigger value="today" data-testid="view-today">Today’s Follow-ups</TabsTrigger>
                        <TabsTrigger value="overdue" data-testid="view-overdue">Overdue</TabsTrigger>
                        <TabsTrigger value="upcoming" data-testid="view-upcoming">Upcoming</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div className="relative md:col-span-2">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search name, mobile, subject" className="pl-9" data-testid="crm-search-input" />
                    </div>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid="crm-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={source} onValueChange={setSource}>
                        <SelectTrigger data-testid="crm-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All sources</SelectItem>
                            {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={ClipboardList} title="No leads" description="New signups and enquiries auto-create leads. You can also add manual leads." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="crm-leads-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Subject / Name</th>
                                <th className="text-left font-medium">Mobile</th>
                                <th className="text-left font-medium">Source</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-left font-medium">Assignee</th>
                                <th className="text-left font-medium">Next Follow-up</th>
                                <th className="text-left p-3 font-medium">Last Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(l => {
                                const overdue = l.status === 'postponed' && l.next_follow_up_at && new Date(l.next_follow_up_at) < new Date();
                                return (
                                    <tr key={l.id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={() => openDrawer(l.id)} data-testid={`lead-row-${l.id}`}>
                                        <td className="p-3">
                                            <div className="font-medium">{l.subject || l.name}</div>
                                            <div className="text-xs text-neutral-500">{l.name}{l.business_name ? ' · ' + l.business_name : ''}</div>
                                        </td>
                                        <td className="font-mono text-xs">{l.mobile}</td>
                                        <td className="text-xs capitalize">{(l.source || '').replace('_', ' ')}</td>
                                        <td><LeadStatusPill status={l.status} /></td>
                                        <td className="text-xs">{l.assignee_name || <span className="text-neutral-400">Unassigned</span>}</td>
                                        <td className={`text-xs ${overdue ? 'text-red-700 font-medium' : ''}`}>
                                            {l.next_follow_up_at ? formatDate(l.next_follow_up_at) : '—'}
                                        </td>
                                        <td className="p-3 text-xs text-neutral-600">{formatDate(l.last_activity_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            <LeadDetailDrawer open={drawerOpen} onOpenChange={setDrawerOpen} leadId={drawerLeadId} assignees={assignees} onChanged={load} />
            <NewLeadDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
        </AppShell>
    );
}
